import { useEffect, useState } from "react";
import { api, colones } from "@/lib/api";

const numCot = (n) => `COT-${String(n || 0).padStart(4, "0")}`;

const fechaCR = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-CR", {
    timeZone: "America/Costa_Rica", day: "2-digit", month: "2-digit", year: "numeric",
  });
};

const manoObraServicio = (sol) => Number(
  sol.mano_obra_efectiva !== undefined && sol.mano_obra_efectiva !== null
    ? sol.mano_obra_efectiva : (sol.mano_obra || 0));

const parseFrt = (s) => {
  const m = (s.nombre || "").match(/^(.*?) — ESTIMADO FRT ([\d.]+)h(?: · MO de: (.*))?$/);
  return m ? { operacion: m[1], horas: m[2], repuesto: m[3] || "" } : { operacion: s.nombre, horas: "", repuesto: "" };
};

/** Arma las filas a imprimir a partir de las solicitudes, con el mismo orden/agrupación que la
 * pantalla (servicio + sus repuestos de plantilla, repuestos sueltos, mano de obra estimada),
 * pero SIN tener en cuenta los grupos colapsados: siempre se imprime todo. */
const construirFilas = (solicitudes) => {
  const presentes = new Set(solicitudes.filter((s) => s.tipo === "servicio").map((s) => s.ref_id));
  const hijosDe = {};
  solicitudes.forEach((s, i) => {
    const m = (s.origen || "").match(/^plantilla:(.+)$/);
    if (m && presentes.has(m[1])) (hijosDe[m[1]] = hijosDe[m[1]] || []).push({ s, i });
  });
  const enGrupo = new Set(Object.values(hijosDe).flat().map((x) => x.i));
  const frt = [];
  solicitudes.forEach((s, i) => { if (s.origen === "frt" || (s.origen || "").startsWith("estimado:")) frt.push({ s, i }); });
  const enFrt = new Set(frt.map((x) => x.i));

  const cuerpo = [];
  const adicionales = [];
  solicitudes.forEach((s, i) => {
    if (enGrupo.has(i) || enFrt.has(i)) return;
    if (s.tipo === "servicio") {
      cuerpo.push({ s, i, rol: "servicio" });
      (hijosDe[s.ref_id] || []).forEach(({ s: hs, i: hi }) => cuerpo.push({ s: hs, i: hi, rol: "repuesto" }));
    } else {
      adicionales.push({ s, i, rol: "repuesto" });
    }
  });
  const estimados = frt.map(({ s, i }) => ({ s, i, rol: "servicio" }));
  return { cuerpo, adicionales, estimados };
};

export const CotizacionImpresion = ({ trabajo, solicitudes, notas }) => {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/config-taller").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const { cuerpo, adicionales, estimados } = construirFilas(solicitudes || []);
  const totalMO = (solicitudes || [])
    .filter((s) => s.tipo === "servicio")
    .reduce((acc, s) => acc + manoObraServicio(s), 0);

  const montoServicio = (s) => (s.cubierta_por && !s.cobrar_igual ? "Incluida" : colones(manoObraServicio(s)));

  const thClase = "py-1.5 px-2 border-b border-black text-left font-semibold";
  const tdClase = "py-1 px-2 align-top";

  const filaServicio = (s, i, nombre) => (
    <tr key={`srv-${i}`} className="font-semibold">
      <td className={tdClase}>{s.codigo || ""}</td>
      <td className={tdClase}>{nombre}</td>
      <td className={tdClase}>—</td>
      <td className={`${tdClase} text-right`}>{montoServicio(s)}</td>
    </tr>
  );

  const filaRepuesto = (s, i) => (
    <tr key={`rep-${i}`}>
      <td className={tdClase}>{s.codigo || ""}</td>
      <td className={`${tdClase} pl-6`}>{s.nombre}</td>
      <td className={tdClase}>{s.cantidad}</td>
      <td className={`${tdClase} text-right`}></td>
    </tr>
  );

  return (
    <div className="hidden print-area cotizacion-impresion text-black bg-white" data-testid="cotizacion-impresion"
      style={{ fontSize: 11 }}>
      <div className="flex items-start justify-between pb-2 border-b border-black">
        <div className="flex items-center gap-2">
          {cfg?.logo && <img src={cfg.logo} alt="Logo" className="h-12 w-12 object-contain" />}
          <div>
            <div className="font-bold" style={{ fontSize: 14 }}>{cfg?.nombre || ""}</div>
            {cfg?.telefono && <div>Tel: {cfg.telefono}</div>}
            {cfg?.direccion && <div>{cfg.direccion}</div>}
          </div>
        </div>
        <div className="text-right">
          <div className="font-bold">Cotización {numCot(trabajo?.numero)}</div>
          <div>{fechaCR(trabajo?.fecha_creacion)}</div>
        </div>
      </div>

      <div className="py-2 space-y-0.5">
        <div><b>Modelo:</b> {trabajo?.modelo || ""}</div>
        {trabajo?.referencia && <div><b>Referencia:</b> {trabajo.referencia}</div>}
        <div><b>Elaborada por:</b> {trabajo?.usuario_ingreso || ""}</div>
      </div>

      <table className="cot-imp-tabla w-full border-collapse" data-testid="cotizacion-impresion-tabla">
        <thead>
          <tr>
            <th className={thClase}>Código</th>
            <th className={thClase}>Descripción</th>
            <th className={thClase}>Cantidad</th>
            <th className={`${thClase} text-right`}>Mano de obra</th>
          </tr>
        </thead>
        <tbody>
          {cuerpo.map((f) => f.rol === "servicio" ? filaServicio(f.s, f.i, f.s.nombre) : filaRepuesto(f.s, f.i))}
          {adicionales.length > 0 && (
            <>
              <tr><td colSpan={4} className={`${tdClase} font-semibold pt-3`}>Repuestos adicionales</td></tr>
              {adicionales.map((f) => filaRepuesto(f.s, f.i))}
            </>
          )}
          {estimados.map((f) => {
            const info = f.s.origen === "frt" ? parseFrt(f.s) : null;
            const nombre = info ? info.operacion : f.s.nombre;
            return filaServicio(f.s, f.i, nombre);
          })}
          <tr className="font-bold">
            <td colSpan={3} className={`${tdClase} text-right border-t border-black`}>Total mano de obra</td>
            <td className={`${tdClase} text-right border-t border-black`} data-testid="cotizacion-impresion-total">
              {colones(totalMO)}
            </td>
          </tr>
        </tbody>
      </table>

      {notas && <div className="mt-3">
        <b>Notas:</b> {notas}
      </div>}
      <div className="mt-3 text-xs">
        Los repuestos se cotizan por separado. Mano de obra sujeta a revisión del vehículo.
      </div>
    </div>
  );
};
